import {
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  onIdTokenChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from "firebase/auth";

import type {
  ChangePasswordFormData,
  LoginFormData,
  SignupFormData,
} from "@/features/auth/schemas/auth";

import { auth } from "../config/firebase";

import type { User } from "firebase/auth";

// Tipos para las respuestas de la API
export interface AuthResponse {
  user: User;
  success: true;
}

export interface AuthError {
  code: string;
  message: string;
  success: false;
}

export type AuthResult = AuthResponse | AuthError;

/**
 * Función para manejar errores de Firebase Auth
 * @param error - El error a manejar
 * @returns {AuthError} El error manejado
 */
function handleAuthError(error: unknown): AuthError {
  if (error && typeof error === "object" && "code" in error) {
    const firebaseError = error as { code: string; message?: string };

    // Mapear códigos de error de Firebase a mensajes en español
    const errorMessages: Record<string, string> = {
      "auth/user-not-found": "Usuario no encontrado",
      "auth/wrong-password": "Contraseña incorrecta",
      "auth/email-already-in-use": "El correo electrónico ya está en uso",
      "auth/weak-password": "La contraseña es demasiado débil",
      "auth/invalid-email": "Correo electrónico inválido",
      "auth/too-many-requests": "Demasiados intentos. Intenta más tarde",
      "auth/network-request-failed": "Error de conexión",
      "auth/user-disabled": "Usuario deshabilitado",
      "auth/operation-not-allowed": "Operación no permitida",
      "auth/invalid-credential": "Credenciales inválidas",
      "auth/requires-recent-login":
        "Por seguridad, vuelve a ingresar tu contraseña actual e intenta de nuevo",
    };

    return {
      code: firebaseError.code,
      message:
        (errorMessages[firebaseError.code] || firebaseError.message) ??
        "Error de autenticación",
      success: false,
    };
  }

  return {
    code: "auth/unknown",
    message: "Error desconocido",
    success: false,
  };
}

/**
 * Función para crear un nuevo usuario
 * @param data - Los datos del usuario a crear
 * @returns {Promise<AuthResult>} El resultado de la operación
 */
export async function signUp(data: SignupFormData): Promise<AuthResult> {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );

    return {
      user: userCredential.user,
      success: true,
    };
  } catch (error) {
    return handleAuthError(error);
  }
}

/**
 * Función para iniciar sesión
 * @param data - Los datos del usuario a iniciar sesión
 * @returns {Promise<AuthResult>} El resultado de la operación
 */
export async function signIn(data: LoginFormData): Promise<AuthResult> {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );

    localStorage.setItem("authToken", await userCredential.user.getIdToken());

    return {
      user: userCredential.user,
      success: true,
    };
  } catch (error) {
    return handleAuthError(error);
  }
}

/**
 * Función para cerrar sesión
 * @returns {Promise<{ success: boolean; error?: string }>} El resultado de la operación
 */
export async function signOutUser(): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    await signOut(auth);
    // Limpiar el token del localStorage al cerrar sesión
    localStorage.removeItem("authToken");
    return { success: true };
  } catch (error) {
    const authError = handleAuthError(error);
    return { success: false, error: authError.message };
  }
}

/**
 * Cambia la contraseña del usuario autenticado, sin correo de reset.
 * Reautentica con la clave actual y luego aplica la nueva (Firebase client SDK).
 */
export async function changePassword(
  data: ChangePasswordFormData
): Promise<{ success: boolean; error?: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: "Usuario no autenticado" };
    }
    if (!user.email) {
      return { success: false, error: "La cuenta no tiene correo asociado" };
    }

    const credential = EmailAuthProvider.credential(
      user.email,
      data.currentPassword
    );
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, data.newPassword);
    return { success: true };
  } catch (error) {
    const authError = handleAuthError(error);
    return { success: false, error: authError.message };
  }
}

/**
 * Función para enviar email de restablecimiento de contraseña
 * @param email - El email del usuario a restablecer contraseña
 * @returns {Promise<{ success: boolean; error?: string }>} El resultado de la operación
 */
export async function resetPassword(
  email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    const authError = handleAuthError(error);
    return { success: false, error: authError.message };
  }
}

/**
 * Función para obtener el usuario actual
 * @returns {User | null} El usuario actual
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Función para escuchar cambios en el estado de autenticación
 * @param callback - La función a ejecutar cuando el estado de autenticación cambia
 * @returns {() => void} La función para desuscribirse
 */
export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Función para escuchar cambios en el token ID de Firebase
 * @param callback - La función a ejecutar cuando el token ID cambia
 * @returns {() => void} La función para desuscribirse
 */
export function onIdTokenChange(callback: (user: User | null) => void): () => void {
  return onIdTokenChanged(auth, callback);
}

/**
 * Función para obtener un token fresco del usuario actual
 * @param forceRefresh - Si debe forzar la renovación del token
 * @returns {Promise<string | null>} El token fresco o null si no hay usuario
 */
export async function getFreshToken(
  forceRefresh: boolean = false
): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }

  try {
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error("Error obteniendo token fresco:", error);
    return null;
  }
}

/**
 * Función para verificar si el usuario está autenticado
 * @returns {boolean} true si el usuario está autenticado, false en caso contrario
 */
export function isAuthenticated(): boolean {
  return auth.currentUser !== null;
}
